import { useState } from "react";

export type DeviationLevel = "match" | "minor" | "major" | "added" | "missing";

export interface PlugComparisonRow {
  plug_number: number;
  planned_type?: string;
  planned_top_ft?: number;
  planned_bottom_ft?: number;
  planned_sacks?: number;
  actual_type?: string;
  actual_top_ft?: number;
  actual_bottom_ft?: number;
  actual_sacks?: number;
  actual_cement_class?: string;
  planned_cement_class?: string;
  actual_tagged_depth_ft?: number;
  // WP5 fields
  planned_placement_method?: string;
  actual_placement_method?: string;
  planned_woc_hours?: number;
  actual_woc_hours?: number;
  actual_woc_tagged?: boolean;
  deviation_level: DeviationLevel;
  deviation_notes: string[];
}

export interface ReconciliationSummary {
  total_planned: number;
  total_actual: number;
  matches: number;
  minor_deviations: number;
  major_deviations: number;
  added_plugs: number;
  missing_plugs: number;
  overall_status: string;
}

interface PlugReconciliationProps {
  comparisons: PlugComparisonRow[];
  summary: ReconciliationSummary;
}

const DEVIATION_CONFIG: Record<
  DeviationLevel,
  { label: string; rowBg: string; badge: string; dot: string }
> = {
  match: {
    label: "Match",
    rowBg: "bg-green-50/50 dark:bg-green-900/5",
    badge: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    dot: "bg-green-500",
  },
  minor: {
    label: "Minor",
    rowBg: "bg-amber-50/50 dark:bg-amber-900/5",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  major: {
    label: "Major",
    rowBg: "bg-red-50/50 dark:bg-red-900/5",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    dot: "bg-red-500",
  },
  added: {
    label: "Added",
    rowBg: "bg-blue-50/50 dark:bg-blue-900/5",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  missing: {
    label: "Missing",
    rowBg: "bg-gray-50 dark:bg-white/[0.02]",
    badge: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
    dot: "bg-gray-400",
  },
};

function StatusBadge({ level }: { level: DeviationLevel }) {
  const cfg = DEVIATION_CONFIG[level];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function fmt(v: number | undefined): string {
  return v !== undefined ? v.toLocaleString() : "—";
}

/** Convert snake_case placement method to display string, e.g. "perf_and_squeeze" → "Perf & Squeeze" */
function fmtMethod(v: string | undefined): string {
  if (!v) return "—";
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace(/ And /g, " & ");
}

export default function PlugReconciliation({ comparisons, summary }: PlugReconciliationProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (plugNumber: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(plugNumber)) {
        next.delete(plugNumber);
      } else {
        next.add(plugNumber);
      }
      return next;
    });
  };

  const overallStatusBg =
    summary.major_deviations > 0
      ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800"
      : summary.minor_deviations > 0
      ? "bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800"
      : "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800";

  const overallStatusText =
    summary.major_deviations > 0
      ? "text-red-800 dark:text-red-300"
      : summary.minor_deviations > 0
      ? "text-amber-800 dark:text-amber-300"
      : "text-green-800 dark:text-green-300";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Plug Reconciliation
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Planned (NOI) vs Actual (DWR) comparison
        </p>
      </div>

      {/* Summary bar */}
      <div className={`mx-6 mt-4 rounded-xl border p-4 ${overallStatusBg}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className={`text-sm font-semibold ${overallStatusText}`}>
            Overall: {summary.overall_status}
          </p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="text-green-700 dark:text-green-400 font-medium">
              {summary.matches} matches
            </span>
            {summary.minor_deviations > 0 && (
              <span className="text-amber-700 dark:text-amber-400 font-medium">
                {summary.minor_deviations} minor
              </span>
            )}
            {summary.major_deviations > 0 && (
              <span className="text-red-700 dark:text-red-400 font-medium">
                {summary.major_deviations} major
              </span>
            )}
            {summary.added_plugs > 0 && (
              <span className="text-blue-700 dark:text-blue-400 font-medium">
                {summary.added_plugs} added
              </span>
            )}
            {summary.missing_plugs > 0 && (
              <span className="text-gray-600 dark:text-gray-400 font-medium">
                {summary.missing_plugs} missing
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
          <span>Planned plugs: {summary.total_planned}</span>
          <span>Actual plugs: {summary.total_actual}</span>
        </div>
      </div>

      {/* Comparison table */}
      <div className="p-4 overflow-x-auto">
        {comparisons.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
            No comparison data available.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/[0.05]">
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" colSpan={4}>
                  Planned (NOI)
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" colSpan={6}>
                  Actual (DWR)
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  Deviation
                </th>
                <th className="px-3 py-2 w-8" />
              </tr>
              <tr className="border-b border-gray-100 dark:border-white/[0.04] bg-gray-50 dark:bg-white/[0.02]">
                <th className="px-3 py-1.5" />
                {/* Planned sub-columns */}
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Type</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Bottom-Top (ft)</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Sacks</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Method</th>
                {/* Actual sub-columns */}
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Type</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Bottom-Top (ft)</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Sacks</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Method</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">WOC (hrs)</th>
                <th className="px-3 py-1.5 text-left text-xs text-gray-400 dark:text-gray-500 font-medium">Tagged</th>
                <th className="px-3 py-1.5" />
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {comparisons.map((row) => {
                const cfg = DEVIATION_CONFIG[row.deviation_level];
                const isExpanded = expandedRows.has(row.plug_number);
                const hasNotes = row.deviation_notes.length > 0;

                // Highlight method cell when planned vs actual differ (both present and different)
                const methodMismatch =
                  row.planned_placement_method !== undefined &&
                  row.actual_placement_method !== undefined &&
                  row.planned_placement_method !== row.actual_placement_method;

                // Highlight WOC cell when actual is below plan (both present)
                const wocBelow =
                  row.planned_woc_hours !== undefined &&
                  row.actual_woc_hours !== undefined &&
                  row.actual_woc_hours < row.planned_woc_hours;

                const amberCell = "bg-amber-100 dark:bg-amber-900/30";

                return (
                  <>
                    <tr
                      key={row.plug_number}
                      className={`transition-colors ${cfg.rowBg} ${hasNotes ? "cursor-pointer hover:opacity-90" : ""}`}
                      onClick={() => hasNotes && toggleRow(row.plug_number)}
                    >
                      <td className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">
                        {row.plug_number}
                      </td>
                      {/* Planned */}
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">
                        {row.planned_type ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {row.planned_top_ft !== undefined
                          ? `${fmt(row.planned_bottom_ft)}–${fmt(row.planned_top_ft)}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">
                        {fmt(row.planned_sacks)}
                      </td>
                      <td className={`px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap ${methodMismatch ? amberCell : ""}`}>
                        {fmtMethod(row.planned_placement_method)}
                      </td>
                      {/* Actual */}
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">
                        {row.actual_type ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {row.actual_top_ft !== undefined
                          ? `${fmt(row.actual_bottom_ft)}–${fmt(row.actual_top_ft)}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">
                        {fmt(row.actual_sacks)}
                      </td>
                      <td className={`px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap ${methodMismatch ? amberCell : ""}`}>
                        {fmtMethod(row.actual_placement_method)}
                      </td>
                      <td className={`px-3 py-2.5 text-gray-700 dark:text-gray-300 ${wocBelow ? amberCell : ""}`}>
                        {row.actual_woc_hours !== undefined ? `${row.actual_woc_hours}` : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.actual_woc_tagged === true ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30">
                            <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        ) : row.actual_woc_tagged === false ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-white/[0.06]">
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      {/* Deviation */}
                      <td className="px-3 py-2.5">
                        <StatusBadge level={row.deviation_level} />
                      </td>
                      <td className="px-3 py-2.5 text-gray-400">
                        {hasNotes && (
                          <svg
                            className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasNotes && (
                      <tr key={`${row.plug_number}-notes`} className={cfg.rowBg}>
                        <td colSpan={13} className="px-3 pb-3 pt-0">
                          <div className="ml-6 rounded-lg bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] p-3">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                              Deviation Notes
                            </p>
                            <ul className="space-y-1">
                              {row.deviation_notes.map((note, i) => (
                                <li key={i} className="text-xs text-gray-700 dark:text-gray-300">
                                  - {note}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
